"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.BaseGrids = void 0;
const color_js_1 = require("@ngageoint/color-js");
const tstl_1 = require("tstl");
const GridConstants_1 = require("./GridConstants");
const GridStyle_1 = require("./GridStyle");
const PropertyConstants_1 = require("./property/PropertyConstants");
/**
 * Grids
 *
 * @param <TGrid>
 *            grid type
 * @param <TZoomGrids>
 *            zoom grids type
 */
class BaseGrids {
    /**
     * Constructor
     *
     * @param properties
     *            grid properties
     */
    constructor(properties) {
        /**
         * Map between zoom levels and grids
         */
        this.zoomGrids = new tstl_1.TreeMap();
        this.properties = properties;
    }
    /**
     * Load the grid
     *
     * @param grid
     *            name
     * @param gridKey
     *            grid name key
     * @param enabled
     *            enable created grids
     * @param labeler
     *            grid labeler
     */
    loadGrid(grid, gridKey, labeler, enabled) {
        if (enabled === null || enabled === undefined) {
            enabled = this.properties.getBooleanProperty(false, PropertyConstants_1.PropertyConstants.GRIDS, gridKey, PropertyConstants_1.PropertyConstants.ENABLED);
            if (enabled === undefined) {
                enabled = true;
            }
        }
        grid.setEnabled(enabled);
        let minZoom = this.properties.getIntegerProperty(false, PropertyConstants_1.PropertyConstants.GRIDS, gridKey, PropertyConstants_1.PropertyConstants.MIN_ZOOM);
        if (minZoom === undefined) {
            minZoom = 0;
        }
        grid.setMinZoom(minZoom);
        const maxZoom = this.properties.getIntegerProperty(false, PropertyConstants_1.PropertyConstants.GRIDS, gridKey, PropertyConstants_1.PropertyConstants.MAX_ZOOM);
        grid.setMaxZoom(maxZoom);
        const linesMinZoom = this.properties.getIntegerProperty(false, PropertyConstants_1.PropertyConstants.GRIDS, gridKey, PropertyConstants_1.PropertyConstants.LINES, PropertyConstants_1.PropertyConstants.MIN_ZOOM);
        grid.setLinesMinZoom(linesMinZoom);
        const linesMaxZoom = this.properties.getIntegerProperty(false, PropertyConstants_1.PropertyConstants.GRIDS, gridKey, PropertyConstants_1.PropertyConstants.LINES, PropertyConstants_1.PropertyConstants.MAX_ZOOM);
        grid.setLinesMaxZoom(linesMaxZoom);
        const colorProperty = this.properties.getProperty(false, this.properties.buildProperty([PropertyConstants_1.PropertyConstants.GRIDS, gridKey, PropertyConstants_1.PropertyConstants.COLOR]));
        const color = colorProperty != null ? color_js_1.Color.color(colorProperty) : color_js_1.Color.black();
        grid.setColor(color);
        let width = this.properties.getDoubleProperty(false, PropertyConstants_1.PropertyConstants.GRIDS, gridKey, PropertyConstants_1.PropertyConstants.WIDTH);
        if (width === undefined) {
            width = this.getDefaultWidth();
        }
        grid.setWidth(width);
        if (labeler) {
            this.loadLabeler(labeler, gridKey);
        }
        grid.setLabeler(labeler);
    }
    /**
     * Load the labeler
     *
     * @param labeler
     *            labeler
     * @param gridKey
     *            grid name key
     */
    loadLabeler(labeler, gridKey) {
        const enabled = this.properties.getBooleanProperty(false, PropertyConstants_1.PropertyConstants.GRIDS, gridKey, PropertyConstants_1.PropertyConstants.LABELER, PropertyConstants_1.PropertyConstants.ENABLED);
        labeler.setEnabled(enabled !== null && enabled !== undefined && enabled);
        const minZoom = this.properties.getIntegerProperty(false, PropertyConstants_1.PropertyConstants.GRIDS, gridKey, PropertyConstants_1.PropertyConstants.LABELER, PropertyConstants_1.PropertyConstants.MIN_ZOOM);
        if (minZoom !== undefined) {
            labeler.setMinZoom(minZoom);
        }
        const maxZoom = this.properties.getIntegerProperty(false, PropertyConstants_1.PropertyConstants.GRIDS, gridKey, PropertyConstants_1.PropertyConstants.LABELER, PropertyConstants_1.PropertyConstants.MAX_ZOOM);
        if (maxZoom !== undefined) {
            labeler.setMaxZoom(maxZoom);
        }
        const color = this.properties.getProperty(false, this.properties.buildProperty([
            PropertyConstants_1.PropertyConstants.GRIDS,
            gridKey,
            PropertyConstants_1.PropertyConstants.LABELER,
            PropertyConstants_1.PropertyConstants.COLOR,
        ]));
        if (color) {
            labeler.setColor(color_js_1.Color.color(color));
        }
        const textSize = this.properties.getDoubleProperty(false, PropertyConstants_1.PropertyConstants.GRIDS, gridKey, PropertyConstants_1.PropertyConstants.LABELER, PropertyConstants_1.PropertyConstants.TEXT_SIZE);
        if (textSize !== undefined) {
            labeler.setTextSize(textSize);
        }
        const buffer = this.properties.getDoubleProperty(false, PropertyConstants_1.PropertyConstants.GRIDS, gridKey, PropertyConstants_1.PropertyConstants.LABELER, PropertyConstants_1.PropertyConstants.BUFFER);
        if (buffer !== undefined) {
            labeler.setBuffer(buffer);
        }
    }
    /**
     * Load the grid style color
     *
     * @param gridKey
     *            grid name key
     * @param gridKey2
     *            second grid name key
     * @return color
     */
    loadGridStyleColor(gridKey, gridKey2) {
        const colorProperty = this.properties.getProperty(false, this.properties.buildProperty([PropertyConstants_1.PropertyConstants.GRIDS, gridKey, gridKey2, PropertyConstants_1.PropertyConstants.COLOR]));
        let color;
        if (colorProperty) {
            color = color_js_1.Color.color(colorProperty);
        }
        return color;
    }
    /**
     * Load the grid style width
     *
     * @param gridKey
     *            grid name key
     * @param gridKey2
     *            second grid name key
     * @return width
     */
    loadGridStyleWidth(gridKey, gridKey2) {
        return this.properties.getDoubleProperty(false, this.properties.buildProperty([PropertyConstants_1.PropertyConstants.GRIDS, gridKey, gridKey2, PropertyConstants_1.PropertyConstants.WIDTH]));
    }
    /**
     * Get a combined grid style from the provided color, width, and grid
     *
     * @param grid
     *            grid
     * @param color
     *            color
     * @param width
     *            width
     * @return grid style
     */
    getGridStyle(grid, color, width) {
        if (!color) {
            color = grid.getColor();
        }
        if (width === null || width === undefined) {
            width = grid.getWidth();
        }
        return GridStyle_1.GridStyle.style(color, width);
    }
    /**
     * Create the zoom level grids
     */
    createZoomGrids() {
        for (let zoom = 0; zoom <= GridConstants_1.GridConstants.MAX_MAP_ZOOM_LEVEL; zoom++) {
            this.createZoomGridsFromZoom(zoom);
        }
    }
    /**
     * Get the grids for the zoom level
     *
     * @param zoom
     *            zoom level
     * @return grids
     */
    getGrids(zoom) {
        let grids = this.zoomGrids.get(zoom);
        if (!grids) {
            grids = this.createZoomGridsFromZoom(zoom);
        }
        return grids;
    }
    /**
     * Create grids for the zoom level
     *
     * @param zoom
     *            zoom level
     * @return grids
     */
    createZoomGridsFromZoom(zoom) {
        const zoomLevelGrids = this.newZoomGrids(zoom);
        for (const grid of this.grids()) {
            if (grid.isEnabled() && grid.isWithin(zoom)) {
                zoomLevelGrids.addGrid(grid);
            }
        }
        this.zoomGrids.set(zoom, zoomLevelGrids);
        return zoomLevelGrids;
    }
    /**
     * Enable grids
     *
     * @param grids
     *            grids
     */
    enableGrids(grids) {
        for (const grid of grids) {
            this.enable(grid);
        }
    }
    /**
     * Disable grids
     *
     * @param grids
     *            grids
     */
    disableGrids(grids) {
        for (const grid of grids) {
            this.disable(grid);
        }
    }
    /**
     * Enable the grid
     *
     * @param grid
     *            grid
     */
    enable(grid) {
        if (!grid.isEnabled()) {
            grid.setEnabled(true);
            const minZoom = grid.getMinZoom();
            let maxZoom = grid.getMaxZoom();
            if (maxZoom === null || maxZoom === undefined) {
                maxZoom = this.zoomGrids.end().value.first;
            }
            for (let zoom = minZoom; zoom <= maxZoom; zoom++) {
                this.addGrid(grid, zoom);
            }
        }
    }
    /**
     * Disable the grid
     *
     * @param grid
     *            grid
     */
    disable(grid) {
        if (grid.isEnabled()) {
            grid.setEnabled(false);
            const minZoom = grid.getMinZoom();
            let maxZoom = grid.getMaxZoom();
            if (maxZoom === null || maxZoom === undefined) {
                maxZoom = this.zoomGrids.end().value.first;
            }
            for (let zoom = minZoom; zoom <= maxZoom; zoom++) {
                this.removeGrid(grid, zoom);
            }
        }
    }
    /**
     * Set the grid minimum zoom
     *
     * @param grid
     *            grid
     * @param minZoom
     *            minimum zoom
     */
    setMinZoom(grid, minZoom) {
        let maxZoom = grid.getMaxZoom();
        if (maxZoom !== null && maxZoom !== undefined && maxZoom < minZoom) {
            maxZoom = minZoom;
        }
        this.setZoomRange(grid, minZoom, maxZoom);
    }
    /**
     * Set the grid maximum zoom
     *
     * @param grid
     *            grid
     * @param maxZoom
     *            maximum zoom
     */
    setMaxZoom(grid, maxZoom) {
        let minZoom = grid.getMinZoom();
        if (maxZoom !== null && maxZoom !== undefined && minZoom > maxZoom) {
            minZoom = maxZoom;
        }
        this.setZoomRange(grid, minZoom, maxZoom);
    }
    /**
     * Set the grid zoom range
     *
     * @param grid
     *            grid
     * @param minZoom
     *            minimum zoom
     * @param maxZoom
     *            maximum zoom
     */
    setZoomRange(grid, minZoom, maxZoom) {
        if (maxZoom !== null && maxZoom !== undefined && maxZoom < minZoom) {
            throw new Error("Min zoom '" + minZoom + "' can not be larger than max zoom '" + maxZoom + "'");
        }
        // All grids zoom range
        const allGridsMin = this.zoomGrids.begin().value.first;
        const allGridsMax = this.zoomGrids.end().value.first;
        // Existing grid zoom range
        const gridMinZoom = grid.getMinZoom();
        let gridMaxZoom = grid.getMaxZoom();
        if (gridMaxZoom === null || gridMaxZoom === undefined) {
            gridMaxZoom = allGridsMax;
        }
        else {
            gridMaxZoom = Math.min(gridMaxZoom, allGridsMax);
        }
        grid.setMinZoom(minZoom);
        grid.setMaxZoom(maxZoom);
        minZoom = Math.max(minZoom, allGridsMin);
        if (maxZoom === null || maxZoom === undefined) {
            maxZoom = allGridsMax;
        }
        else {
            maxZoom = Math.min(maxZoom, allGridsMax);
        }
        const minOverlap = Math.max(minZoom, gridMinZoom);
        const maxOverlap = Math.min(maxZoom, gridMaxZoom);
        const overlaps = minOverlap <= maxOverlap;
        if (overlaps) {
            const min = Math.min(minZoom, gridMinZoom);
            const max = Math.max(maxZoom, gridMaxZoom);
            for (let zoom = min; zoom <= max; zoom++) {
                if (zoom < minOverlap || zoom > maxOverlap) {
                    if (zoom >= minZoom && zoom <= maxZoom) {
                        this.addGrid(grid, zoom);
                    }
                    else {
                        this.removeGrid(grid, zoom);
                    }
                }
            }
        }
        else {
            for (let zoom = gridMinZoom; zoom <= gridMaxZoom; zoom++) {
                this.removeGrid(grid, zoom);
            }
            for (let zoom = minZoom; zoom <= maxZoom; zoom++) {
                this.addGrid(grid, zoom);
            }
        }
    }
    /**
     * Add a grid to the zoom level
     *
     * @param grid
     *            grid
     * @param zoom
     *            zoom level
     */
    addGrid(grid, zoom) {
        const grids = this.zoomGrids.get(zoom);
        if (grids) {
            grids.addGrid(grid);
        }
    }
    /**
     * Remove a grid from the zoom level
     *
     * @param grid
     *            grid
     * @param zoom
     *            zoom level
     */
    removeGrid(grid, zoom) {
        const grids = this.zoomGrids.get(zoom);
        if (grids) {
            grids.removeGrid(grid);
        }
    }
    /**
     * Enable all grid labelers
     */
    enableAllLabelers() {
        for (const grid of this.grids()) {
            const labeler = grid.getLabeler();
            if (labeler) {
                labeler.setEnabled(true);
            }
        }
    }
    /**
     * Set all label grid edge buffers
     *
     * @param buffer
     *            label buffer (greater than or equal to 0.0 and less than 0.5)
     */
    setAllLabelBuffers(buffer) {
        for (const grid of this.grids()) {
            const labeler = grid.getLabeler();
            if (labeler) {
                labeler.setBuffer(buffer);
            }
        }
    }
}
exports.BaseGrids = BaseGrids;
//# sourceMappingURL=BaseGrids.js.map